import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, CheckCircle2, FileText,
  Shield, Download, Loader2, ExternalLink, ScrollText, Hash
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  getMyProfile, getLLCStates, getLLCGuide, setupLLC,
  generateHardshipLetter, generateOperatingAgreement, generateEINGuide
} from '@/lib/api';

const fadeIn = { hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

export default function LLCWorkflow() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('formation');
  const [step, setStep] = useState('select_state');
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingLetter, setGeneratingLetter] = useState(false);
  const [letterText, setLetterText] = useState('');

  // Operating Agreement
  const [generatingAgreement, setGeneratingAgreement] = useState(false);
  const [agreementText, setAgreementText] = useState('');

  // EIN
  const [generatingEIN, setGeneratingEIN] = useState(false);
  const [einGuide, setEINGuide] = useState(null);

  const [hardshipForm, setHardshipForm] = useState({
    full_name: '', state: '', current_employment: '', monthly_income: '',
    monthly_expenses: '', dependents: 0, hardship_reason: '', additional_details: ''
  });

  useEffect(() => {
    if (!localStorage.getItem('rtb_user_token')) { navigate('/login'); return; }
    Promise.all([getMyProfile(), getLLCStates()]).then(([profile, statesData]) => {
      setUser(profile);
      setStates(statesData.states);
      setHardshipForm(f => ({ ...f, full_name: `${profile.first_name} ${profile.last_name}` }));
      if (profile.llc_state) {
        setSelectedState(profile.llc_state);
        setBusinessName(profile.llc_business_name || '');
        getLLCGuide(profile.llc_state).then(g => { setGuide(g); setStep('guide'); });
      }
    }).catch(() => navigate('/login'));
  }, [navigate]);

  const handleSetupLLC = async () => {
    if (!selectedState || !businessName.trim()) { toast.error('Please select a state and enter your business name'); return; }
    setLoading(true);
    try {
      const result = await setupLLC({ state: selectedState, business_name: businessName });
      setGuide(result.guide);
      setStep('guide');
      toast.success('LLC formation started!');
    } catch { toast.error('Failed to start LLC setup'); }
    finally { setLoading(false); }
  };

  const handleGenerateHardship = async () => {
    if (!hardshipForm.full_name || !hardshipForm.state || !hardshipForm.hardship_reason) {
      toast.error('Please fill in all required fields'); return;
    }
    setGeneratingLetter(true);
    try {
      const result = await generateHardshipLetter(hardshipForm);
      setLetterText(result.letter);
      setStep('letter');
      toast.success('Letter generated by AI assistant!');
    } catch { toast.error('Failed to generate letter'); }
    finally { setGeneratingLetter(false); }
  };

  const handleGenerateAgreement = async () => {
    if (!user?.llc_state || !user?.llc_business_name) {
      toast.error('Please complete LLC formation setup first'); return;
    }
    setGeneratingAgreement(true);
    try {
      const result = await generateOperatingAgreement();
      setAgreementText(result.agreement);
      toast.success('Operating Agreement generated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate agreement'); }
    finally { setGeneratingAgreement(false); }
  };

  const handleGenerateEIN = async () => {
    if (!user?.llc_state || !user?.llc_business_name) {
      toast.error('Please complete LLC formation setup first'); return;
    }
    setGeneratingEIN(true);
    try {
      const result = await generateEINGuide();
      setEINGuide(result);
      toast.success('EIN guide generated!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate EIN guide'); }
    finally { setGeneratingEIN(false); }
  };

  const handleDownload = (text, filename) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Downloaded');
  };

  const selectedStateInfo = states.find(s => s.code === selectedState);
  const llcStarted = user?.llc_state && user?.llc_business_name;

  const selectClass = "w-full h-12 bg-gray-50 border border-gray-200 text-gray-900 px-4 rounded-xl focus:border-rtb-purple focus:outline-none text-sm";

  const TABS = [
    { id: 'formation', label: 'LLC Formation', icon: Shield },
    { id: 'agreement', label: 'Operating Agreement', icon: ScrollText },
    { id: 'ein', label: 'EIN Assistant', icon: Hash },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="llc-workflow">
      <header className="bg-white border-b border-gray-100 px-6 py-4 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto flex items-center gap-4">
          <Link to="/portal" className="text-gray-400 hover:text-gray-600 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-rtb-purple" />
            <span className="font-oswald font-bold text-gray-900">LLC & Business Setup</span>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="border-b border-gray-100 bg-white">
        <div className="max-w-5xl mx-auto flex gap-1 px-6">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-rtb-purple text-rtb-purple'
                  : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
              data-testid={`llc-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">

          {/* === FORMATION TAB === */}
          {activeTab === 'formation' && (
            <motion.div key="formation" initial="hidden" animate="visible" exit="hidden" variants={fadeIn}>
              {step === 'select_state' && (
                <div data-testid="state-select-step">
                  <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-2">Start Your LLC</h2>
                  <p className="text-gray-500 mb-8">Choose your state and business name to begin.</p>
                  <div className="space-y-4 max-w-lg">
                    <div>
                      <Label className="text-gray-600 mb-2 block text-sm font-medium">State</Label>
                      <select value={selectedState} onChange={(e) => { setSelectedState(e.target.value); setHardshipForm(f => ({ ...f, state: e.target.value })); }}
                        className={selectClass} data-testid="llc-state-select">
                        <option value="">Select your state</option>
                        {states.map(s => (
                          <option key={s.code} value={s.code}>{s.name} — Filing Fee: {s.fee} {s.waiver ? '(Waiver Available)' : ''}</option>
                        ))}
                      </select>
                    </div>
                    {selectedStateInfo && (
                      <div className="p-4 bg-white border border-gray-100 rounded-xl">
                        <p className="text-sm"><span className="text-gray-400">Filing Fee:</span> <span className="text-rtb-purple font-bold">{selectedStateInfo.fee}</span></p>
                        <p className="text-sm"><span className="text-gray-400">Fee Waiver:</span> {selectedStateInfo.waiver ? <span className="text-rtb-green font-semibold">Available</span> : <span className="text-gray-400">Not available</span>}</p>
                      </div>
                    )}
                    <div>
                      <Label className="text-gray-600 mb-2 block text-sm font-medium">Business Name</Label>
                      <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                        className="input-styled" placeholder="Your Business Name LLC" data-testid="llc-business-name" />
                    </div>
                    <Button onClick={handleSetupLLC} disabled={loading} className="btn-primary" data-testid="start-llc-btn">
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Begin Formation <ArrowRight className="ml-2 w-5 h-5" /></>}
                    </Button>
                  </div>
                </div>
              )}

              {step === 'guide' && guide && (
                <div data-testid="llc-guide-step">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                    <div>
                      <h2 className="text-2xl font-oswald font-bold text-gray-900">LLC Formation — {guide.state_name}</h2>
                      <p className="text-gray-500 text-sm">Filing Agency: {guide.filing_agency} | Fee: {guide.filing_fee}</p>
                    </div>
                    {guide.waiver_available && (
                      <Button onClick={() => setStep('hardship')} variant="outline" className="border-rtb-purple/30 text-rtb-purple rounded-full" data-testid="hardship-btn">
                        <FileText className="mr-2 w-4 h-4" /> AI Fee Waiver Assistant
                      </Button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {guide.steps.map((s, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                        className="p-5 bg-white border border-gray-100 rounded-xl flex gap-4 shadow-sm" data-testid={`llc-step-${s.step}`}>
                        <div className="w-10 h-10 bg-rtb-purple/10 flex items-center justify-center flex-shrink-0 rounded-xl">
                          <span className="font-oswald font-bold text-rtb-purple">{s.step}</span>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 mb-1">{s.title}</h3>
                          <p className="text-gray-500 text-sm">{s.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  {!guide.waiver_available && (
                    <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                      <p className="text-gray-400 text-sm">Fee waiver is not typically available in {guide.state_name}, but you can still try the AI assistant.</p>
                      <Button onClick={() => setStep('hardship')} variant="outline" className="mt-3 border-gray-200 text-gray-500 text-sm rounded-full">
                        Try AI Fee Waiver Assistant
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {step === 'hardship' && (
                <div data-testid="hardship-form">
                  <button onClick={() => setStep('guide')} className="text-gray-400 hover:text-gray-600 text-sm mb-6 flex items-center gap-1">
                    <ArrowLeft className="w-4 h-4" /> Back to Guide
                  </button>
                  <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-2">AI Fee Waiver Assistant</h2>
                  <p className="text-gray-500 mb-8">Answer these questions and our AI will create a professional letter for your LLC filing fee waiver.</p>
                  <div className="space-y-4 max-w-lg">
                    <div>
                      <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Full Legal Name *</Label>
                      <Input value={hardshipForm.full_name} onChange={(e) => setHardshipForm({ ...hardshipForm, full_name: e.target.value })}
                        className="input-styled" data-testid="hl-name" />
                    </div>
                    <div>
                      <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Current Employment Status *</Label>
                      <select value={hardshipForm.current_employment} onChange={(e) => setHardshipForm({ ...hardshipForm, current_employment: e.target.value })}
                        className={selectClass} data-testid="hl-employment">
                        <option value="">Select</option>
                        <option value="Unemployed">Unemployed</option>
                        <option value="Part-time employed">Part-time Employed</option>
                        <option value="Full-time employed (low income)">Full-time (Low Income)</option>
                        <option value="Self-employed (struggling)">Self-employed (Struggling)</option>
                        <option value="Disabled">Disabled</option>
                        <option value="Student">Student</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Monthly Income *</Label>
                        <Input value={hardshipForm.monthly_income} onChange={(e) => setHardshipForm({ ...hardshipForm, monthly_income: e.target.value })}
                          className="input-styled" placeholder="$0" data-testid="hl-income" />
                      </div>
                      <div>
                        <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Monthly Expenses *</Label>
                        <Input value={hardshipForm.monthly_expenses} onChange={(e) => setHardshipForm({ ...hardshipForm, monthly_expenses: e.target.value })}
                          className="input-styled" placeholder="$0" data-testid="hl-expenses" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Number of Dependents</Label>
                      <Input type="number" value={hardshipForm.dependents} onChange={(e) => setHardshipForm({ ...hardshipForm, dependents: parseInt(e.target.value) || 0 })}
                        className="input-styled" min="0" data-testid="hl-dependents" />
                    </div>
                    <div>
                      <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Primary Reason *</Label>
                      <select value={hardshipForm.hardship_reason} onChange={(e) => setHardshipForm({ ...hardshipForm, hardship_reason: e.target.value })}
                        className={selectClass} data-testid="hl-reason">
                        <option value="">Select</option>
                        <option value="Job loss or unemployment">Job Loss / Unemployment</option>
                        <option value="Medical expenses or disability">Medical Expenses / Disability</option>
                        <option value="Single parent with limited income">Single Parent, Limited Income</option>
                        <option value="Recently released from incarceration">Recently Released</option>
                        <option value="Natural disaster or housing displacement">Natural Disaster / Displacement</option>
                        <option value="Low income below poverty line">Low Income / Below Poverty Line</option>
                        <option value="Other financial hardship">Other Financial Hardship</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-gray-600 mb-1.5 block text-sm font-medium">Additional Details</Label>
                      <textarea value={hardshipForm.additional_details} onChange={(e) => setHardshipForm({ ...hardshipForm, additional_details: e.target.value })}
                        className="w-full min-h-[100px] bg-gray-50 border border-gray-200 text-gray-900 p-3 rounded-xl focus:border-rtb-purple focus:outline-none text-sm"
                        placeholder="Share any additional context..." data-testid="hl-details" />
                    </div>
                    <Button onClick={handleGenerateHardship} disabled={generatingLetter} className="btn-primary" data-testid="generate-letter-btn">
                      {generatingLetter ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> AI Generating...</> : <><FileText className="mr-2 w-5 h-5" /> Generate Letter</>}
                    </Button>
                  </div>
                </div>
              )}

              {step === 'letter' && (
                <div data-testid="letter-result">
                  <button onClick={() => setStep('hardship')} className="text-gray-400 hover:text-gray-600 text-sm mb-6 flex items-center gap-1">
                    <ArrowLeft className="w-4 h-4" /> Edit Answers
                  </button>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-oswald font-bold text-gray-900">Your AI-Generated Letter</h2>
                    <Button onClick={() => handleDownload(letterText, `fee_waiver_letter_${selectedState}.txt`)} variant="outline" className="border-rtb-purple/30 text-rtb-purple rounded-full" data-testid="download-letter-btn">
                      <Download className="mr-2 w-4 h-4" /> Download
                    </Button>
                  </div>
                  <div className="p-6 bg-white border border-gray-100 rounded-xl whitespace-pre-wrap text-sm text-gray-700 leading-relaxed max-h-[600px] overflow-y-auto shadow-sm" data-testid="letter-content">
                    {letterText}
                  </div>
                  <div className="mt-6 flex gap-4">
                    <Button onClick={() => setStep('hardship')} variant="outline" className="border-gray-200 text-gray-500 rounded-full">Regenerate</Button>
                    <Link to="/portal"><Button className="btn-primary">Back to Portal <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* === OPERATING AGREEMENT TAB === */}
          {activeTab === 'agreement' && (
            <motion.div key="agreement" initial="hidden" animate="visible" exit="hidden" variants={fadeIn}>
              <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-2">Operating Agreement Generator</h2>
              <p className="text-gray-500 mb-8">
                Generate a customized Single-Member LLC Operating Agreement for your business.
                {llcStarted && <span className="text-rtb-purple font-semibold"> ({user?.llc_business_name} — {states.find(s => s.code === user?.llc_state)?.name})</span>}
              </p>

              {!llcStarted ? (
                <div className="p-8 bg-white border border-gray-100 rounded-xl text-center">
                  <ScrollText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">Please complete LLC formation setup first.</p>
                  <Button onClick={() => setActiveTab('formation')} className="btn-primary text-sm">Go to LLC Formation</Button>
                </div>
              ) : !agreementText ? (
                <div className="max-w-lg">
                  <div className="p-5 bg-white border border-gray-100 rounded-xl mb-6 space-y-2">
                    <p className="text-sm"><span className="text-gray-400">LLC Name:</span> <span className="font-semibold text-gray-900">{user?.llc_business_name}</span></p>
                    <p className="text-sm"><span className="text-gray-400">State:</span> <span className="font-semibold text-gray-900">{states.find(s => s.code === user?.llc_state)?.name}</span></p>
                    <p className="text-sm"><span className="text-gray-400">Member:</span> <span className="font-semibold text-gray-900">{user?.first_name} {user?.last_name}</span></p>
                  </div>
                  <p className="text-gray-400 text-sm mb-6">
                    Our AI will generate a professional Operating Agreement tailored to your LLC and state laws.
                    It will include placeholder fields for items you need to fill in.
                  </p>
                  <Button onClick={handleGenerateAgreement} disabled={generatingAgreement} className="btn-primary" data-testid="generate-agreement-btn">
                    {generatingAgreement ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating Agreement...</> : <><ScrollText className="mr-2 w-5 h-5" /> Generate Operating Agreement</>}
                  </Button>
                </div>
              ) : (
                <div data-testid="agreement-result">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="font-oswald font-bold text-lg text-gray-900">Your Operating Agreement</h3>
                    <Button onClick={() => handleDownload(agreementText, `operating_agreement_${user?.llc_business_name?.replace(/\s/g, '_')}.txt`)} variant="outline" className="border-rtb-purple/30 text-rtb-purple rounded-full" data-testid="download-agreement-btn">
                      <Download className="mr-2 w-4 h-4" /> Download
                    </Button>
                  </div>
                  <div className="p-6 bg-white border border-gray-100 rounded-xl whitespace-pre-wrap text-sm text-gray-700 leading-relaxed max-h-[600px] overflow-y-auto shadow-sm" data-testid="agreement-content">
                    {agreementText}
                  </div>
                  <div className="mt-6">
                    <Button onClick={() => { setAgreementText(''); handleGenerateAgreement(); }} variant="outline" className="border-gray-200 text-gray-500 rounded-full">Regenerate</Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* === EIN ASSISTANT TAB === */}
          {activeTab === 'ein' && (
            <motion.div key="ein" initial="hidden" animate="visible" exit="hidden" variants={fadeIn}>
              <h2 className="text-2xl font-oswald font-bold text-gray-900 mb-2">EIN Application Assistant</h2>
              <p className="text-gray-500 mb-8">
                Get a personalized, step-by-step guide to apply for your Employer Identification Number (EIN) from the IRS.
              </p>

              {!llcStarted ? (
                <div className="p-8 bg-white border border-gray-100 rounded-xl text-center">
                  <Hash className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 mb-4">Please complete LLC formation setup first.</p>
                  <Button onClick={() => setActiveTab('formation')} className="btn-primary text-sm">Go to LLC Formation</Button>
                </div>
              ) : !einGuide ? (
                <div className="max-w-lg">
                  <div className="p-5 bg-white border border-gray-100 rounded-xl mb-6 space-y-2">
                    <p className="text-sm"><span className="text-gray-400">LLC Name:</span> <span className="font-semibold text-gray-900">{user?.llc_business_name}</span></p>
                    <p className="text-sm"><span className="text-gray-400">State:</span> <span className="font-semibold text-gray-900">{states.find(s => s.code === user?.llc_state)?.name}</span></p>
                    <p className="text-sm"><span className="text-gray-400">Owner:</span> <span className="font-semibold text-gray-900">{user?.first_name} {user?.last_name}</span></p>
                  </div>
                  <p className="text-gray-400 text-sm mb-6">
                    Our AI will generate a detailed, personalized guide for applying for your EIN — 
                    including exactly what to select on each screen of the IRS online application.
                  </p>
                  <Button onClick={handleGenerateEIN} disabled={generatingEIN} className="btn-primary" data-testid="generate-ein-btn">
                    {generatingEIN ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating Guide...</> : <><Hash className="mr-2 w-5 h-5" /> Generate EIN Guide</>}
                  </Button>
                </div>
              ) : (
                <div data-testid="ein-result">
                  <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                    <h3 className="font-oswald font-bold text-lg text-gray-900">Your EIN Application Guide</h3>
                    <div className="flex gap-3">
                      <a href={einGuide.irs_url} target="_blank" rel="noopener noreferrer">
                        <Button className="bg-rtb-green text-white hover:bg-rtb-green/90 rounded-full text-sm px-5" data-testid="irs-link-btn">
                          <ExternalLink className="mr-2 w-4 h-4" /> Apply at IRS.gov
                        </Button>
                      </a>
                      <Button onClick={() => handleDownload(einGuide.guide, `ein_guide_${user?.llc_business_name?.replace(/\s/g, '_')}.txt`)} variant="outline" className="border-rtb-purple/30 text-rtb-purple rounded-full text-sm" data-testid="download-ein-btn">
                        <Download className="mr-2 w-4 h-4" /> Download
                      </Button>
                    </div>
                  </div>
                  <div className="p-6 bg-white border border-gray-100 rounded-xl whitespace-pre-wrap text-sm text-gray-700 leading-relaxed max-h-[600px] overflow-y-auto shadow-sm" data-testid="ein-content">
                    {einGuide.guide}
                  </div>
                  <div className="mt-6">
                    <Button onClick={() => { setEINGuide(null); handleGenerateEIN(); }} variant="outline" className="border-gray-200 text-gray-500 rounded-full">Regenerate</Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}
